import * as icons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from "react";

import { addTags } from '../api/ApiService';
import { type Tag } from "../api/models";
import { useAppConfig } from "../config/useAppConfig";
import { useTagDialog } from "../dialog/use-tag-dialog";
import type { Entry } from "../store/entry";
import { classNames } from "../utils/class-names";
import { formatDate, humanizeBytes, humanizeDuration } from "../utils/format";
import { MediaViewDisableFlags } from "./MediaViewPage";
import { FeatureFlags } from '../config/AppConfig';

export const Details = ({entry, dispatch}: {entry: Entry, dispatch: any}) => {
  const appConfig = useAppConfig()
  const disabledFeatures = appConfig.disabled || [] as FeatureFlags
  const disabledFlags = appConfig.pages?.mediaView?.disabled || [] as MediaViewDisableFlags
  const dateFormat = appConfig.format?.date || '%d.%m.%y'
  const timeFormat = appConfig.format?.time || '%H:%M:%S'
  const {openDialog, setDialogVisible} = useTagDialog()

  if (!entry) {
    return (<></>)
  }

  const dispatchSearch = (query: string) => {
    dispatch({type: 'search', query})
  }

  const escapeSearchValue = (value: string) => /[\s+]/.test(value) ? `"${value}"` : value

  const queryTerm = (key, value, op?) => {
    let query
    if (key && value && op) {
      query = `${key}${op}${escapeSearchValue(value)}`
    } else if (key && value) {
      query = `${key}:${escapeSearchValue(value)}`
    } else if (key) {
      query = `${escapeSearchValue(key)}`
    }
    return query
  }

  const searchLink = (text, query) => {
    return <a className="text-gray-300 break-all rounded hover:cursor-pointer hover:bg-gray-600 hover:text-gray-200" key={query} onClick={() => dispatchSearch(query)} title={`Search for '${query}'`}>{text}</a>
  }

  const simpleSearchLink = (text, key?, value?) => {
    let query = queryTerm(key || text, value)
    return searchLink(text, query)
  }

  const joinReducer = c => (prev, cur) => prev.length ? [prev, c, cur] : [cur]

  const sepSpan = sep => {
    return (
      <span className="px-1">{sep}</span>
    )
  }

  const mapFile = file => {
    const indexTerm = queryTerm('index', file.index)
    const isDownloadable = !!appConfig.sources?.find((source) => source.downloadable && source.indexName === file.index);

    const filename = file.filename
    const links: React.JSX.Element[] = []
    let lastPos = 0
    filename.replace(/[\\/]/g, (sep, pos) => {
      const name = filename.substring(lastPos, pos)
      const path = filename.substring(0, pos)
      const pathTerm = queryTerm('path', path, '~')
      links.push(searchLink(name, `${indexTerm} ${pathTerm}`))
      links.push(sepSpan(sep))
      lastPos = pos + 1
    })
    const basename = filename.substring(lastPos)
    links.push(searchLink(basename, queryTerm('filename', basename, '~')))

    return [
      simpleSearchLink(file.index, `index:${file.index}`),
      sepSpan(':'),
      ...links,
      ` ${humanizeBytes(file.size)}`,
      isDownloadable && (
        <a href={`api/sources/${file.index}/${file.filename.replaceAll(/\\/g, '/')}`} target="_blank" className="px-1 text-gray-300 break-all rounded hover:cursor-pointer hover:bg-gray-600 hover:text-gray-200" title={`Click to download original file ${file.filename}`}>
          download <FontAwesomeIcon icon={icons.faArrowUpRightFromSquare} className="pl-1 text-gray-400 hover:text-gray-200"/>
        </a>
      ),
    ]
  }

  const mainFileData = entry.files[0];
  const mainFilename = mainFileData.filename.replace(/.*[/\\]/g, '')

  const hasAddress = entry => entry.road || entry.city || entry.country

  const hasGeo = entry => entry.latitude || entry.longitute

  const GeoLink = entry => {
    const { latitude, longitude } = entry
    if (!latitude || !longitude) {
      return null
    }

    return (
      <a key={`${entry.shortId}-geo`} className="text-gray-300 break-all rounded hover:cursor-pointer hover:bg-gray-600 hover:text-gray-200" onClick={() => dispatch({type: 'map'})}>
        {latitude.toFixed(4)},{longitude.toFixed(4)} (open map)
      </a>
    )
  }

  const origTags: Tag[] = (entry.tags || []).map(name => ({name, remove: false}))

  const onSubmit = ({tags}: {tags: Tag[]}) => {
    const tagNames = tags.map(({name}) => name)
    const origTagNames = entry.tags || []
    const tagActions = origTagNames.filter(name => !tagNames.includes(name)).map(name => ({name, remove: true}))
    tagActions.push(...tagNames.filter(name => !origTagNames.includes(name)).map(name => ({name, remove: false})))
    if (tagActions.length) {
      addTags([entry.id], tagActions).then(() => {
        setDialogVisible(false);
      })
    } else {
      setDialogVisible(false);
    }

    return false;
  }

  const editTags = () => {
    openDialog({initialTags: origTags, onSubmit})
  }

  function copyShareUrlToClipboard(e) {
    e.preventDefault();
    navigator.clipboard.writeText(getShareUrl());
  }

  function getShareUrl() {
    const url = new URL("../../share/" + entry.shortId, window.location.href);
    return url.toString();
  }

  return (
    <>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl text-gray-300">Media Details</h3>
          <a className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-700 hover:cursor-pointer"
            onClick={() => dispatch({type: 'toggleDetails'})}
            title="Close media details">
            <FontAwesomeIcon icon={icons.faXmark} className="text-gray-700 hover:text-gray-400 active:text-gray-200"/>
          </a>
        </div>
        <div className="flex flex-col gap-6 text-gray-500">
          <div className="flex">
            <div className="w-8">
              <FontAwesomeIcon icon={icons.faIdCard} className="text-gray-300"/>
            </div>
            <div>
              <p>{mainFilename}</p>
              <p>{simpleSearchLink(entry.type, 'type', entry.type)} {entry.id.substring(0, 7)}</p>
              <p>{entry.duration > 0 && (
                <>{humanizeDuration(entry.duration)}, </>
              )}{entry.width}x{entry.height}</p>
            </div>
          </div>
          <div className="flex">
            <div className="w-8">
              <FontAwesomeIcon icon={icons.faCalendar} className="text-gray-300"/>
            </div>
            <div>
              <p>
                <DateFormat entry={entry} format={dateFormat} searchLink={searchLink}/>
              </p>
              <p>
                {formatDate(timeFormat, entry.date)}
              </p>
            </div>
          </div>
          <div className="flex">
            <div className="flex-shrink-0 w-8">
              <FontAwesomeIcon icon={icons.faFolder} className="text-gray-300"/>
            </div>
            <div>
              {entry.files.map(mapFile).map(l => (
                <>
                  <p>{l}</p>
                </>
              ))}
            </div>
          </div>
          { !disabledFlags.includes('map') && (hasAddress(entry) || hasGeo(entry)) && (
            <div className="flex">
              <div className="flex-shrink-0 w-8">
                <FontAwesomeIcon icon={icons.faMapPin} className="text-gray-300"/>
              </div>
              <div>
                {entry.road && (
                  <p>{simpleSearchLink(entry.road, 'road', entry.road)}</p>
                )}
                {entry.city && (
                  <p>{simpleSearchLink(entry.city, 'city', entry.city)}</p>
                )}
                {entry.country && (
                  <p>{simpleSearchLink(entry.country, 'country', entry.country)}</p>
                )}
                {hasGeo(entry) && (
                  <p className={classNames({'mt-2': hasAddress(entry)})}>{GeoLink(entry)}</p>
                )}
              </div>
            </div>
          )}
          { !!entry.description && (
            <>
              <div className="flex">
                <div className="flex-shrink-0 w-8">
                  <FontAwesomeIcon icon={icons.faNewspaper} className="text-gray-300"/>
                </div>
                <div>
                  <p className="inline-flex flex-wrap gap-2">{entry.description}</p>
                </div>
              </div>
            </>
          )}
          {!disabledFlags.includes('tag') && (entry.tags?.length > 0 || !(disabledFlags.includes('edit') || disabledFeatures.includes('edit'))) && (<div className="flex">
            <div className="flex-shrink-0 w-8">
              <FontAwesomeIcon icon={icons.faTags} className="text-gray-300"/>
            </div>
            <div>
              <p className="inline-flex flex-wrap gap-2">
                {entry.tags.map(tag => (
                  <a className="px-2 py-1 text-gray-300 bg-gray-800 rounded hover:bg-gray-700 hover:text-gray-200 hover:cursor-pointer" onClick={() => dispatchSearch(`${queryTerm("tag", tag)}`)} title={`Search for tag ${tag}`}>{tag}</a>
                ))}
                {!disabledFlags.includes('edit') && !disabledFeatures?.includes('edit') && (
                  <a className="flex items-center gap-2 px-2 py-1 text-gray-500 bg-transparent border border-gray-700 rounded group inset-1 hover:bg-gray-700 hover:text-gray-200 hover:cursor-pointer active:bg-gray-600" onClick={editTags} title={`Edit single tags`}>
                    <FontAwesomeIcon icon={icons.faPen} className="text-gray-500 group-hover:text-gray-300"/>
                    <span>Edit tags</span>
                  </a>
                )}
              </p>
            </div>
          </div>)}
          <div className="flex">
            <div className="flex-shrink-0 w-8">
              <FontAwesomeIcon icon={icons.faCamera} className="text-gray-300"/>
            </div>
            <div>
              { (entry.make || entry.model) && (
                <p>
                  {entry.make && (
                    <>{simpleSearchLink(entry.make, 'make', entry.make)}</>
                  )}
                  {entry.make && entry.model && (
                    <><span className="px-2">/</span></>
                  )}
                  {entry.model && (
                    <>{simpleSearchLink(entry.model, 'model', entry.model)}</>
                  )}
                </p>
              )}
              <p>ISO {entry.iso}, Aperture {entry.aperture}, Shutter Speed {entry.ShutterSpeedRaw}, Focal Length {entry.focalLength}mm</p>
            </div>
          </div>
          { !disabledFlags.includes('annotation') && entry.objects?.length > 0 && (
            <div className="flex">
              <div className="flex-shrink-0 w-8">
                <FontAwesomeIcon icon={icons.faShapes} className="text-gray-300"/>
              </div>
              <div>
                <p className="inline-flex flex-wrap gap-2">{entry.objects.map(object => (
                  <span>{simpleSearchLink(object.class)} ({object.score})</span>
                ))}</p>
              </div>
            </div>
          )}
          { !disabledFlags.includes('annotation') && !!entry.faces && entry.faces.length > 0 && (
            <div className="flex">
              <div className="flex-shrink-0 w-8">
                <FontAwesomeIcon icon={icons.faUser} className="text-gray-300"/>
              </div>
              <div>
                <p className="inline-flex flex-wrap gap-2">{entry.faces.map(face => (
                  <span>{face.gender} (~{face.age.toFixed()}y)</span>
                ))}</p>
              </div>
            </div>
          )}
          <div className="flex">
            <div className="flex-shrink-0 w-8">
              <FontAwesomeIcon icon={icons.faShareNodes} className="text-gray-300"/>
            </div>
            <div>
              <p className="inline-flex flex-wrap gap-2">
                  <a className="flex items-center gap-2 px-1 py-0 text-gray-500 bg-transparent border border-gray-700 rounded group inset-1 hover:bg-gray-700 hover:text-gray-200 hover:cursor-pointer active:bg-gray-600" onClick={copyShareUrlToClipboard} href={getShareUrl()} title={`Click to copy shareable link to clipboard`}>
                    <span>Share media</span>
                  </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function DateFormat({entry, format, searchLink}) {
  const result: React.ReactNode[] = []

  if (!entry?.date) {
    return result
  }

  let last = 0;
  let pos = format.indexOf('%', last)

  while (pos >= 0 && pos < format.length - 1) {
    if (pos > last) {
      const head = (<span className="px-1">{format.substring(last, pos)}</span>)
      result.push(head)
    }
    const code = format.substring(pos, pos + 2)

    let link: React.JSX.Element | null = null
    switch (code) {
      case '%Y':
        link = searchLink(formatDate(code, entry.date), `year:${entry.date.substr(0, 4)}`)
        break
      case '%y':
        link = searchLink(formatDate(code, entry.date), `year:${entry.date.substr(0, 4)}`)
        break
      case '%m':
        link = searchLink(formatDate(code, entry.date), `year:${entry.date.substr(0, 4)} month:${entry.date.substr(5, 2)}`)
        break
      case '%d':
        link = searchLink(formatDate(code, entry.date), `year:${entry.date.substr(0, 4)} month:${entry.date.substr(5, 2)} day:${entry.date.substr(8, 2)}`)
        break
      default:
        link = <span className="px-1">{formatDate(code, entry.date)}</span>
    }

    if (link) {
      result.push(link)
    }

    last = pos + 2
    pos = format.indexOf('%', last)
  }

  if (last < format.length) {
    const tail = (<span className="px-1">{format.substring(last)}</span>)
    result.push(tail)
  }

  return result
}